import { useMemo, useState, type ReactNode } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { usePortalTicketsNav } from "@/hooks/usePortalTicketsNav";
import {
  PortalTicketsImpersonationProvider,
  usePortalTicketsImpersonation,
} from "@/contexts/PortalTicketsImpersonationContext";
import { AccionesUsuarioCabecera, FilaVerComoMovil } from "@/components/admin/AccionesUsuarioCabecera";
import { PortalTicketsImpersonationSelector } from "./PortalTicketsImpersonationSelector";
import { usePerfilVista } from "@/hooks/usePerfilVista";
import { TicketsProvider } from "@/lib/portal-tickets/tickets-store";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { AnalistaFlotante } from "@/pages/admin/portal-analitica/Analista";
import { useAgenteIa } from "@/hooks/useGuiaPortal";

/**
 * El Asistente de tickets es un agente administrable (`ia_agentes`, tipo analista, Sistema →
 * Asistentes IA). Aparece en todos los menús del portal si `ia_agentes_disponibles()` trae un
 * analista para la ruta (rol con acceso y agente activo) y además el usuario tiene la Analítica:
 * la EF analista-tickets exige los dos permisos, así que sin la Analítica el botón solo daría
 * "sin permiso".
 */
const RUTA_ANALITICA = "/admin/portal-tickets/analitica";

const PortalTicketsLayoutInner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const canImpersonate = profile?.puede_impersonar === true;
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const { items, isLoading: navCargando } = usePortalTicketsNav();
  const [mobileOpen, setMobileOpen] = useState(false);
  const agenteAnalista = useAgenteIa(location.pathname, "analista");
  const conAnalitica = items.some((i) => i.path === RUTA_ANALITICA);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const currentSection = useMemo(
    () => items.find((i) => isActive(i.path))?.label ?? "Portal Tickets",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, location.pathname],
  );

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Perfil: con "Ver como", la persona impersonada (nombre y rol suyos), no el admin.
  const perfil = usePerfilVista({ rol: profile?.rol_nombre ?? "Usuario" });
  const userName = perfil.nombre.split(/\s+/).slice(0, 2).join(" ");
  const userRole = perfil.rol || "Usuario";

  const sidebarContent = (
    <PortalSidebar
      portal="Portal Tickets"
      miCuenta
      cargando={navCargando}
      groups={[{
        items: items.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => handleNavigate(item.path),
        })),
      }]}
      usuario={{ nombre: userName, rol: userRole, iniciales: perfil.iniciales }}
      onRegresar={canReturnToAdmin ? () => handleNavigate("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
    <div className="flex min-h-screen antialiased">
      <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
        {sidebarContent}
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
          {sidebarContent}
        </SheetContent>
      </Sheet>

      <div className="min-w-0 flex-1 lg:pl-64">
        <header className="sticky top-0 z-20 hidden h-14 items-center justify-between border-b border-border bg-card px-6 lg:flex">
          <h1 className="truncate text-xl font-bold tracking-tight text-foreground">{currentSection}</h1>
          <AccionesUsuarioCabecera verComo={canImpersonate ? <PortalTicketsImpersonationSelector /> : undefined} />
        </header>

        <header className="sticky top-0 z-20 flex flex-col border-b border-border bg-card lg:hidden">
          <div className="flex h-14 items-center justify-between px-3">
            <div className="flex min-w-0 items-center gap-2">
              <button
                onClick={() => setMobileOpen(true)}
                className="-ml-1 rounded-md p-2 text-foreground hover:bg-muted"
                aria-label="Abrir menú"
              >
                <Menu className="h-5 w-5" />
              </button>
              <p className="truncate text-[15px] font-semibold tracking-tight text-foreground">
                {currentSection}
              </p>
            </div>
            <AccionesUsuarioCabecera compacto />
          </div>
          {/* "Ver como" en móvil: segunda fila, igual que Cobranza y Productos. */}
          {canImpersonate && (
            <FilaVerComoMovil>
              <PortalTicketsImpersonationSelector />
            </FilaVerComoMovil>
          )}
        </header>
        <main className="min-w-0 overflow-x-clip min-h-[calc(100vh-56px)] bg-background px-4 py-5 sm:px-8">
          <Outlet />
        </main>
        {agenteAnalista && conAnalitica && <AnalistaFlotante agente={agenteAnalista} />}
      </div>
    </div>
  );
};

/**
 * "Ver como" (20261022094554): con un usuario impersonado la lista es la suya, armada en el servidor
 * con SU regla de fila (`get_tickets_vista_como`), y el store queda en solo lectura. Sin
 * impersonar, `vistaUid` no se manda y todo es la sesión, como siempre.
 */
const TicketsProviderConVista = ({ children }: { children: ReactNode }) => {
  const { impersonatedUser } = usePortalTicketsImpersonation();
  return impersonatedUser?.id ? (
    <TicketsProvider vistaUid={impersonatedUser.id}>{children}</TicketsProvider>
  ) : (
    <TicketsProvider>{children}</TicketsProvider>
  );
};

export const PortalTicketsLayout = () => (
  <PortalTicketsImpersonationProvider>
    <TicketsProviderConVista>
      <PortalTicketsLayoutInner />
    </TicketsProviderConVista>
  </PortalTicketsImpersonationProvider>
);

export default PortalTicketsLayout;